import React, { useContext, useEffect, useRef, useState } from "react";

import ProductItem from "../ProductItem/ProductItem";
import ThemeContext from "~/contexts/ThemeContext";
import Button from "~/components/Button/Button";
import OrderContext from "~/contexts/OrderContext";
import TaxContext from "~/contexts/TaxContext";
import SiteContext from "~/contexts/SiteContext";
import { getCartItemCount } from "~/helpers/cartHelpers";

import styles from "./ProductsList.module.css";
import Modal from "~/components/Modal/Modal";
import { CartPosition } from "~/entities/templateEntities";
import DesktopCart from "~/modules/cart/components/DesktopCart/DesktopCart";
import { Menu, MenuTabItem } from "~/entities/menuEntities";
import { CategoryFontSize } from "~/theme/models/themeModels";
import { appConfig } from "~/configs/appConfig";

type ProductsListProps = {
  productsListRef?: React.RefObject<HTMLDivElement>;
  menu: Menu;
  onSelectItem: (item: MenuTabItem) => void;
  onMobileCheckout: VoidFunction;
};

const ProductsList: React.FC<ProductsListProps> = ({
  productsListRef,
  menu,
  onSelectItem,
  onMobileCheckout,
}) => {
  const [lastTabMinHeight, setLastTabMinHeight] = useState<string>();
  const [visibleResetCartModal, setVisibleResetCartModal] = useState(false);

  const mobileFooterRef = useRef<HTMLDivElement>(null);

  const { template } = useContext(ThemeContext);
  const { orderSummary, reset } = useContext(OrderContext);
  const { formatAmount, taxes } = useContext(TaxContext);
  const { site } = useContext(SiteContext);

  const isLoading = !site || !taxes;

  // Style
  const backgroundColor = template.menuScreen.general.backgroundColour;
  const showCart =
    template.cart.position === CartPosition.OnTheSide || appConfig.WebView;
  const categoryStyles: React.CSSProperties = {
    color: template.menuScreen.category.fontColour,
    fontSize: CategoryFontSize[template.menuScreen.category.fontSize],
    fontFamily: template.menuScreen.category.fontFamily,
    fontWeight: template.menuScreen.category.fontWeight,
    textTransform: template.menuScreen.category.fontTransform,
  };
  const mobileCounterStyles: React.CSSProperties = {
    color: template.global.button.primaryButtonColour,
    backgroundColor: template.global.button.primaryButtonFontColour,
  };

  useEffect(() => {
    if (typeof document === "undefined") return;

    const lastTabMinHeight = `calc(100vh - ${[
      '[data-sticky="header"]',
      '[data-sticky="offline-banner"]',
      '[data-sticky="menu-tabs"]',
    ].reduce((acc, selector) => {
      const element = document.querySelector(selector);
      return acc + (element?.getBoundingClientRect().height || 0);
    }, 0)}px)`;

    setLastTabMinHeight(lastTabMinHeight);
  }, []);

  return (
    <div
      className={styles.wrapper}
      style={
        {
          ["--mobile-footer-height"]: `${
            mobileFooterRef.current?.getBoundingClientRect().height
          }px`,
        } as Record<string, any>
      }
    >
      <div className={styles.menu}>
        <div ref={productsListRef} className={styles.tabs}>
          {!!lastTabMinHeight &&
            menu.tabs.map((tab, index) => (
              <div
                id={tab.code}
                key={tab.code}
                className={styles.tab}
                style={{
                  columnGap: showCart ? 16 : 42,
                  minHeight:
                    menu.tabs.length - 1 === index ? lastTabMinHeight : "auto",
                }}
              >
                <div className={styles.tabName} style={categoryStyles}>
                  {tab.name}
                </div>
                {tab.items.map((item) => (
                  <ProductItem
                    key={item.productCode}
                    product={item.productData}
                    onClick={() => onSelectItem(item)}
                  />
                ))}
              </div>
            ))}
        </div>
        {showCart && <DesktopCart />}
      </div>
      <div
        ref={mobileFooterRef}
        className={styles.mobileFooter}
        style={{ backgroundColor }}
      >
        <Button
          variant="secondary"
          onClick={() => setVisibleResetCartModal(true)}
          disabled={isLoading || orderSummary.lines.length === 0}
        >
          <span>Reset</span>
        </Button>
        <Button
          onClick={onMobileCheckout}
          disabled={isLoading || orderSummary.lines.length === 0}
          variant="primary"
          className={styles.viewOrderButton}
        >
          <div className={styles.counter} style={mobileCounterStyles}>
            {getCartItemCount(orderSummary.lines)}
          </div>
          View Order{" "}
          <b>
            {formatAmount(isLoading ? 0 : orderSummary?.finalPriceIncTax ?? 0)}
          </b>
        </Button>
      </div>
      <Modal
        title="Are you sure you want to empty your cart?"
        visible={visibleResetCartModal}
        onClose={() => setVisibleResetCartModal(false)}
        onConfirm={reset}
      />
    </div>
  );
};

export default ProductsList;
